// client/src/components/common/ThemedSelect.tsx (TypeScript since ST-00216)
import { Children, isValidElement, useMemo, type ReactElement, type ReactNode } from 'react';
import Select, {
  type CSSObjectWithLabel,
  type GroupBase,
  type OnChangeValue,
  type Props as SelectProps,
  type StylesConfig,
} from 'react-select';
import { themedSelectStyles } from '../../utils/selectStyles';

/** An <option> value as callers pass it (numbers are matched by their string form). */
export type ThemedSelectValue = string | number;

/** One react-select option built from an <option> child. */
export interface ThemedSelectOption {
  value: ThemedSelectValue;
  label: ReactNode;
  isDisabled: boolean;
  /** Never set: lets a flat option and an <optgroup> group be told apart by `options`. */
  options?: undefined;
}

/** An <optgroup> child. */
export interface ThemedSelectGroup extends GroupBase<ThemedSelectOption> {
  label: string;
  options: ThemedSelectOption[];
}

/** The synthetic event onChange fires with, like a native select's. */
export interface ThemedSelectChangeEvent {
  target: {
    name: string | undefined;
    /** The picked value ('' when cleared); an array of values when `isMulti`. */
    value: ThemedSelectValue | ThemedSelectValue[];
  };
}

/** The props ThemedSelect reads itself; everything else is passed to react-select. */
interface ThemedSelectOwnProps {
  value?: ThemedSelectValue | readonly ThemedSelectValue[] | null;
  onChange?: (e: ThemedSelectChangeEvent) => void;
  /** <option> / <optgroup> elements, as for a native <select>. */
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  placeholder?: ReactNode;
  isClearable?: boolean;
  isMulti?: boolean;
  /** Accepted for native-select parity; not passed on (react-select has no `required`). */
  required?: boolean;
}

export type ThemedSelectProps = ThemedSelectOwnProps &
  Omit<
    Partial<SelectProps<ThemedSelectOption, boolean, ThemedSelectGroup>>,
    keyof ThemedSelectOwnProps
  >;

/** The props of an <option> / <optgroup> child that are read here. */
interface OptionChildProps {
  value?: ThemedSelectValue;
  children?: ReactNode;
  label?: string;
  disabled?: boolean;
  hidden?: boolean;
}

/**
 * An option's text the way `HTMLOptionElement.text` reads it: the text of its children, ASCII
 * whitespace collapsed to one space and trimmed.
 */
const optionText = (children: ReactNode): string => {
  const parts: string[] = [];
  const walk = (node: ReactNode): void => {
    if (node === null || node === undefined || typeof node === 'boolean') return;
    if (typeof node === 'string' || typeof node === 'number' || typeof node === 'bigint') {
      parts.push(String(node));
    } else if (Array.isArray(node)) {
      node.forEach(walk);
    } else if (isValidElement<{ children?: ReactNode }>(node)) {
      walk(node.props.children);
    }
  };
  walk(children);
  return parts
    .join('')
    .replace(/[\t\n\f\r ]+/g, ' ')
    .trim();
};

/**
 * An option's value as a native <select> reports it: the `value` attribute, or the option's
 * text when there is none (ST-00230: it used to be `undefined`, and inside an <optgroup> the
 * option was dropped).
 */
const optionValue = (props: OptionChildProps): ThemedSelectValue =>
  props.value !== undefined ? props.value : optionText(props.children);

// Tailwind scale → px (1 unit = 4px). Cover the values this codebase actually uses.
const PX: Readonly<Record<string, number>> = {
  0: 0,
  1: 4,
  2: 8,
  2.5: 10,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 28,
  8: 32,
  9: 36,
  10: 40,
  11: 44,
  12: 48,
  14: 56,
};
const HEIGHT_PX = PX;
const TEXT_SIZE: Readonly<Record<string, string>> = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
};

// Strip classes that would conflict with react-select's own control styling.
// The control's border/bg/focus/padding/height/font-size are handled via the
// styles prop — so these classes are redundant (and cause double-borders /
// height mismatches) when applied to the wrapper div.
const CONFLICTING_PREFIXES = [
  'border',
  'rounded',
  'shadow',
  'bg-',
  'focus:',
  'hover:',
  'ring-',
  'px-',
  'py-',
  'p-',
  'pl-',
  'pr-',
  'pt-',
  'pb-',
  'text-sm',
  'text-base',
  'text-xs',
  'text-lg',
  'h-',
  'min-h-',
  'max-h-',
  'appearance-',
];
const stripConflictingClasses = (className: string): string => {
  if (!className || typeof className !== 'string') return className;
  return className
    .split(/\s+/)
    .filter((cls) => {
      if (!cls) return false;
      return !CONFLICTING_PREFIXES.some((prefix) =>
        prefix.endsWith('-') || prefix.endsWith(':')
          ? cls.startsWith(prefix)
          : cls === prefix || cls.startsWith(prefix + '-') || cls.startsWith(prefix + ':')
      );
    })
    .join(' ');
};

interface Sizing {
  heightPx: number | null | undefined;
  fontSize: string | null | undefined;
  paddingLeft: number | null | undefined;
  paddingRight: number | null | undefined;
}

// Extract size/padding hints from a className string so we can apply them
// directly to the react-select control (the native select's padding used to
// leave room for overlaid icons; we need to mirror that on the control).
const extractSizing = (className: string): Partial<Sizing> => {
  if (!className) return {};
  const heightMatch = className.match(/\bh-(\d+)\b/);
  const heightPx = heightMatch ? HEIGHT_PX[heightMatch[1]!] : null;
  const textMatch = className.match(/(?:^|\s)text-(xs|sm|base|lg)(?:\s|$)/);
  const fontSize = textMatch ? TEXT_SIZE[textMatch[1]!] : null;
  const plMatch = className.match(/\bpl-(\d+(?:\.\d+)?)\b/);
  const prMatch = className.match(/\bpr-(\d+(?:\.\d+)?)\b/);
  const paddingLeft = plMatch ? PX[plMatch[1]!] : null;
  const paddingRight = prMatch ? PX[prMatch[1]!] : null;
  return { heightPx, fontSize, paddingLeft, paddingRight };
};

type StyleFn = (base: CSSObjectWithLabel, state: unknown) => CSSObjectWithLabel;

/*
 * Drop-in replacement for a native <select> with <option> children.
 * Props mirror native select: value, onChange(event), children (<option>/<optgroup>),
 * disabled, id, name, className, placeholder.
 *
 * onChange fires with a synthetic { target: { name, value } } so existing
 * handlers written for native selects keep working without changes.
 *
 * The first option with an empty-string value ("All assets", "-- None --",
 * "Select haulier...") is the placeholder shown while the value is empty AND,
 * like an option in a native <select>, a choice in the menu — picking it emits
 * ''. Before ST-00215 it was placeholder-only, so once a value was picked an
 * "All …" filter could never go back to All, and an optional field could never
 * be cleared again (audit PLT-13). Mark it `disabled` or `hidden` to keep it a
 * prompt only. Multi-selects keep it as the placeholder only.
 */
const ThemedSelect = ({
  value,
  onChange,
  children,
  className = '',
  disabled = false,
  id,
  name,
  placeholder,
  isClearable,
  isMulti = false,
  required: _required, // taken out so it is not passed on to react-select
  ...rest
}: ThemedSelectProps) => {
  const { options, placeholderText } = useMemo(() => {
    const opts: Array<ThemedSelectOption | ThemedSelectGroup> = [];
    let placeholderLabel: ReactNode = null;

    // Returns true when the option should also be listed as a normal choice.
    const takeEmptyOption = (props: OptionChildProps) => {
      if (placeholderLabel !== null) return true; // a second '' option: plain choice
      placeholderLabel = props.children;
      return !isMulti && !props.disabled && !props.hidden;
    };

    Children.forEach(children, (node) => {
      const child = node as ReactElement<OptionChildProps> | null | undefined;
      if (!child || !child.props) return;

      if (child.type === 'optgroup') {
        const group: ThemedSelectGroup = {
          label: child.props.label as string,
          options: [],
        };
        Children.forEach(child.props.children, (gNode) => {
          const g = gNode as ReactElement<OptionChildProps> | null | undefined;
          if (g && g.type === 'option') {
            const optValue = optionValue(g.props);
            if (optValue !== '' || takeEmptyOption(g.props)) {
              group.options.push({
                value: optValue,
                label: g.props.children,
                isDisabled: !!g.props.disabled,
              });
            }
          }
        });
        if (group.options.length > 0) opts.push(group);
      } else if (child.type === 'option') {
        const optValue = optionValue(child.props);
        if (optValue !== '' || takeEmptyOption(child.props)) {
          opts.push({
            value: optValue,
            label: child.props.children,
            isDisabled: !!child.props.disabled,
          });
        }
      }
    });

    return { options: opts, placeholderText: placeholderLabel as ReactNode };
  }, [children, isMulti]);

  const selected = useMemo(() => {
    const flat: ThemedSelectOption[] = [];
    options.forEach((o) => (o.options ? flat.push(...o.options) : flat.push(o)));
    if (isMulti) {
      // Multi-select value is an array of scalar values (mirrors the single API,
      // just plural). Map each to its option, preserving the caller's order.
      const arr = Array.isArray(value)
        ? (value as readonly ThemedSelectValue[])
        : value === undefined || value === null || value === ''
          ? []
          : [value as ThemedSelectValue];
      return arr
        .map((v) => flat.find((o) => String(o.value) === String(v)))
        .filter(Boolean) as ThemedSelectOption[];
    }
    if (value === undefined || value === null || value === '') return null;
    return flat.find((o) => String(o.value) === String(value)) || null;
  }, [options, value, isMulti]);

  const handleChange = (opt: OnChangeValue<ThemedSelectOption, boolean>) => {
    if (!onChange) return;
    if (isMulti) {
      // react-select passes the full array of selected options (or null on clear).
      // Emit the same { target: { value } } shape as single-select, with an array
      // of scalar values so callers can destructure e.target.value consistently.
      const values = Array.isArray(opt)
        ? (opt as readonly ThemedSelectOption[]).map((o) => o.value)
        : [];
      onChange({ target: { name, value: values } });
      return;
    }
    const single = opt as ThemedSelectOption | null;
    onChange({ target: { name, value: single ? single.value : '' } });
  };

  const wrapperClassName = stripConflictingClasses(className);
  const { heightPx, fontSize, paddingLeft, paddingRight } = extractSizing(className);

  const mergedStyles = useMemo((): StylesConfig<ThemedSelectOption, boolean, ThemedSelectGroup> => {
    if (!heightPx && !fontSize && paddingLeft == null && paddingRight == null) {
      return themedSelectStyles;
    }

    const withOverride =
      (styleFn: StyleFn, extra: CSSObjectWithLabel) =>
      (base: CSSObjectWithLabel, state: unknown) => ({
        ...styleFn(base, state),
        ...extra,
      });

    const compactPadding = heightPx && heightPx <= 32;
    const overridePadding = heightPx != null || paddingLeft != null || paddingRight != null;
    const vcPadLeft = paddingLeft != null ? paddingLeft : compactPadding ? 6 : 8;
    // Leave room for the dropdown indicator (~16px), so pr-8 on the original
    // maps to ~16px on the valueContainer, not 32px.
    const vcPadRight =
      paddingRight != null ? Math.max(paddingRight - 16, 0) : compactPadding ? 2 : 4;

    const valueContainerExtra = {
      ...(heightPx ? { height: heightPx } : {}),
      ...(overridePadding
        ? { paddingLeft: vcPadLeft, paddingRight: vcPadRight, paddingTop: 0, paddingBottom: 0 }
        : {}),
    };

    return {
      ...themedSelectStyles,
      control: withOverride(themedSelectStyles.control, {
        ...(heightPx ? { minHeight: heightPx, height: heightPx } : {}),
        ...(fontSize ? { fontSize } : {}),
      }),
      valueContainer: withOverride(themedSelectStyles.valueContainer, valueContainerExtra),
      indicatorsContainer: (base) => ({
        ...base,
        ...(heightPx ? { height: heightPx } : {}),
      }),
      dropdownIndicator: withOverride(themedSelectStyles.dropdownIndicator, {
        ...(compactPadding ? { padding: 4 } : {}),
      }),
      singleValue: withOverride(themedSelectStyles.singleValue, fontSize ? { fontSize } : {}),
      placeholder: withOverride(themedSelectStyles.placeholder, fontSize ? { fontSize } : {}),
      option: withOverride(themedSelectStyles.option, fontSize ? { fontSize } : {}),
      input: withOverride(themedSelectStyles.input, fontSize ? { fontSize } : {}),
    };
  }, [heightPx, fontSize, paddingLeft, paddingRight]);

  return (
    <Select<ThemedSelectOption, boolean, ThemedSelectGroup>
      inputId={id}
      name={name}
      value={selected}
      onChange={handleChange}
      options={options}
      isDisabled={disabled}
      isClearable={isClearable}
      isMulti={isMulti}
      placeholder={placeholder || placeholderText || 'Select...'}
      classNamePrefix="select"
      styles={mergedStyles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      className={wrapperClassName}
      {...rest}
    />
  );
};

export default ThemedSelect;
